import React, { useEffect, useRef, useState } from 'react';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';

import { useIsMobile } from '../../hooks';

import { getCarouselOptions } from './helpers';

import styles from './LoginCarousel.module.scss';

const LoginCarousel = () => {
  const { isMobile } = useIsMobile();
  const [activeTab, setActiveTab] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const options = getCarouselOptions();
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTouchingRef = useRef<boolean>(false);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-advance control
  const stopAutoScroll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startAutoScroll = () => {
    stopAutoScroll();
    intervalRef.current = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % options.length);
    }, PredefinedMilisecondsTypeEnum.fiveSeconds);
  };

  useEffect(() => {
    startAutoScroll();
    return () => {
      stopAutoScroll();
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
    };
  }, [options.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDots = () =>
    options.map((option, index) => (
      <div
        key={option.title}
        className={index === activeTab ? styles.activeDot : styles.inactiveDot}
        onClick={() => setActiveTab(index)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setActiveTab(index);
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Go to slide ${index + 1}`}
      />
    ));

  // Scroll to active slide (desktop & mobile) based on exact slide width
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const firstSlide = container.firstElementChild as HTMLElement | null;
    const slideWidth = (firstSlide?.clientWidth || container.clientWidth) || 1;
    const targetLeft = Math.round(activeTab * slideWidth);
    container.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }, [activeTab]);

  const onTouchStart = () => {
    isTouchingRef.current = true;
    // Stop the timer while user is interacting
    stopAutoScroll();
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
  };

  const onTouchEnd = () => {
    isTouchingRef.current = false;
    // CSS scroll-snap handles the final snap; index updates via onScroll debounce
    // Restart auto-scroll shortly after snap settles so it starts from the stopped slide
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    restartTimeoutRef.current = setTimeout(() => {
      startAutoScroll();
      restartTimeoutRef.current = null;
    }, 200);
  };

  const onScroll = () => {
    if (!isMobile) return;
    const container = contentRef.current;
    if (!container) return;
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    scrollDebounceRef.current = setTimeout(() => {
      const left = container.scrollLeft;
      const firstSlide = container.firstElementChild as HTMLElement | null;
      const slideWidth = (firstSlide?.clientWidth || container.clientWidth) || 1;
      const idx = Math.round(left / slideWidth);
      const clamped = Math.max(0, Math.min(options.length - 1, idx));
      setActiveTab(clamped);
    }, 120);
  };

  return (
    <>
      {isMobile ? (
        <div className={styles.container} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className={styles.content} ref={contentRef} onScroll={onScroll}>
            {options.map((option) => (
              <div key={option.title} className={styles.imageContainer}>
                <img className={styles.icon} src={option.icon} alt="AceTrade Login" />
                <div className={styles.title}>{option.title}</div>
              </div>
            ))}
          </div>
          <div className={styles.dotsWrapper}>{renderDots()}</div>
        </div>
      ) : (
        <div className={styles.container}>
          <img className={styles.icon} src={options[activeTab].icon} alt="AceTrade Login" />
          <div className={styles.title}>{options[activeTab].title}</div>
          <div className={styles.dotsWrapper}>{renderDots()}</div>
        </div>
      )}
    </>
  );
};

export default LoginCarousel;
